import { useEffect, useState, useCallback } from "react";
import { fetchToken } from "store/guest_user/helper";
import { postFCMToken } from "helpers/jiraya_helper";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useLocalStorage } from "./useLocalStorage";
import { GALLERY_ACCESS_LEVEL } from "@/utils/enums";
import useEvent from "@/helpers/data_hooks.js/useEvent";
import { useGalleryStore } from "@/store/gallery/galleryStore";
import { isNotificationSupported } from "@/helpers/utils";

const MySwal = withReactContent(Swal);

/**
 * Custom hook to manage web notifications
 * Handles:
 * - Service worker registration (Firebase messaging + offline)
 * - FCM token fetching and management
 * - Notification permission handling
 * - Notification prompt UI
 */
export const useWebNotification = (images) => {
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [fcmToken, setFcmToken] = useState(null);
  const [isServiceWorkerRegistered, setIsServiceWorkerRegistered] =
    useState(false);

  const eventKeyType = useGalleryStore((state) => state.eventKeyType);
  const requestId = useGalleryStore((state) => state.requestId);
  const { eventId } = useEvent();

  const [showMorePictureAlert, setShowMorePictureAlert] = useLocalStorage(
    "more-picture-alert",
    true
  );

  // Register service workers on mount
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      // Register Firebase messaging service worker
      navigator.serviceWorker
        .register(
          "/firebase-messaging-sw.js?env=" +
            encodeURIComponent(JSON.stringify(import.meta.env)),
          {
            scope: "/firebase-cloud-messaging-push-scope",
          }
        )
        .then((registration) => {
          console.info(
            "Firebase Service Worker registered with scope:",
            registration.scope
          );
        })
        .catch((err) => {
          console.error("Firebase Service Worker registration failed:", err);
        });

      // Register offline service worker
      navigator.serviceWorker
        .register("/offline-sw.js")
        .then((registration) => {
          console.info(
            "Offline Service Worker registered with scope:",
            registration.scope
          );
          setIsServiceWorkerRegistered(true);
        })
        .catch((err) => {
          console.error("Offline Service Worker registration failed:", err);
        });
    }
  }, []);

  useEffect(() => {
    if (eventKeyType === GALLERY_ACCESS_LEVEL.FACE_ONLY) {
      const pushNotificationTimeout = setTimeout(
        () => {
          if (!Array.isArray(images)) return;

          if (
            isNotificationSupported() &&
            Notification &&
            Notification?.permission !== "denied" &&
            showMorePictureAlert
          ) {
            MySwal.fire({
              html: (
                <div>
                  <h1 style={{ fontSize: "1.5rem" }} className="text-center">
                    {images.length === 0
                      ? "Didnt get any images, Dont worry we will notify you as soon as they're ready.✨📸"
                      : "More Amazing Event Photos are on the way!"}
                  </h1>
                  <p>
                    {
                      "Click Allow Notifications to get notified as soon as they're ready. No spams, just memories. ❤️"
                    }
                  </p>
                </div>
              ),
              confirmButtonText: "Allow Notifications",
              showCancelButton: false,
            })
              .then(() => {
                setShowMorePictureAlert(false);
              })
              .then(() => {
                if (requestId) {
                  fetchToken().then(async (token) => {
                    if (token) {
                      await postFCMToken({
                        request_id: requestId,
                        firebase_token: token,
                      }).then((res) => {
                        console.info("FCM Token posted", res);
                      });
                    }
                  });
                } else {
                  console.error("No request id found");
                }
              });
          }
        },
        // 10 seconds = 10,000 milisec
        10_000
      );
      return () => {
        clearTimeout(pushNotificationTimeout);
      };
    }
  }, [eventId, images?.length]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === "undefined") {
      console.warn("Notifications not supported in this browser");
      return null;
    }

    if (Notification.permission === "granted") {
      return "granted";
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return null;
    }
  }, []);

  // Fetch FCM token
  const getFCMToken = useCallback(async () => {
    try {
      const token = await fetchToken();
      if (token) {
        setFcmToken(token);
        return token;
      }
      return null;
    } catch (error) {
      console.error("Error fetching FCM token:", error);
      return null;
    }
  }, []);

  // Post FCM token to server
  const postFCMTokenToServer = useCallback(
    async (requestId) => {
      if (!requestId) {
        console.warn("No request_id provided for FCM token posting");
        return null;
      }

      try {
        const token = await getFCMToken();
        if (token) {
          const response = await postFCMToken({
            request_id: requestId,
            firebase_token: token,
          });
          console.info("FCM Token posted successfully:", response);
          return response;
        }
        return null;
      } catch (error) {
        console.error("Error posting FCM token:", error);
        return null;
      }
    },
    [getFCMToken]
  );

  // Show notification prompt with SweetAlert2
  const showNotificationPrompt = useCallback(
    async (requestId, options) => {
      try {
        if (options?.heading && !fcmToken) {
          MySwal.fire({
            html: (
              <div className="p-2 rounded-4">
                <h3 style={{ fontSize: "1.5rem" }} className="text-center">
                  {options?.heading}
                </h3>
                <p>{options?.body}</p>
              </div>
            ),
            confirmButtonText: "Allow Notifications",
            showCancelButton: false,
          }).then(async () => {
            // Request permission first
            const permission = await requestNotificationPermission();
            if (permission === "granted") {
              // Post FCM token to server
              await postFCMTokenToServer(requestId);
            } else {
              console.warn("Notification permission not granted:", permission);
            }
          });
        }

        return true;
      } catch (error) {
        console.error("Error showing notification prompt:", error);
        return null;
      }
    },
    [requestNotificationPermission, postFCMTokenToServer]
  );

  return {
    notificationPermission,
    fcmToken,
    isServiceWorkerRegistered,
    requestNotificationPermission,
    getFCMToken,
    postFCMTokenToServer,
    showNotificationPrompt,
    showMorePictureAlert,
    setShowMorePictureAlert,
  };
};

export default useWebNotification;
